<script setup lang="ts">
import { ref, watch } from 'vue';
import { Dialog, Button, useToast } from 'primevue';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { useSettingStore } from 'shared/setting/stores/settingStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { computeStorageAreaDisplayText } from 'shared/storageAreas/domain/computeDisplayText';
import { language as shapeLanguage } from 'shared/mapStyles/domain/shapeFormulaLanguage';
import FormulaEditor from '@/common/components/FormulaEditor.vue';
import { useSaveSettings } from '@/settings/api/saveSettings';
import type { SettingRequest } from '@/settings/api/models/settingRequest';
import { useUpdateDisplayTexts, type StorageAreaDisplayText } from '@/storageAreas/api/updateDisplayTexts';

const visible = defineModel<boolean>('visible', { required: true });

const toast = useToast();
const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);
const settingStore = useSettingStore();
const storageAreaStore = useStorageAreaStore();
const saveSettings = useSaveSettings();
const updateDisplayTexts = useUpdateDisplayTexts();

const formula = ref<string>(settingStore.setting.sharedClient.storageArea.displayTextFormula);
const saving = ref<boolean>(false);

watch(visible, (value) => {
  if (value) {
    formula.value = settingStore.setting.sharedClient.storageArea.displayTextFormula;
  }
});

async function save() {
  saving.value = true;
  try {
    const newSetting: SettingRequest = {
      sharedClient: {
        storageArea: {
          nameFormat: settingStore.setting.sharedClient.storageArea.nameFormat,
          displayTextFormula: formula.value,
          logYardLength: settingStore.setting.sharedClient.storageArea.logYardLength,
          logYardWidth: settingStore.setting.sharedClient.storageArea.logYardWidth,
          logYardSideArea: settingStore.setting.sharedClient.storageArea.logYardSideArea,
        },
      },
    };

    const response = await saveSettings.execute(newSetting);
    if (!response.ok) {
      return;
    }

    settingStore.setting = response.setting;

    const updates: StorageAreaDisplayText[] = [];
    for (const storageArea of storageAreaStore.storageAreas) {
      updates.push({
        id: storageArea.id,
        displayText: computeStorageAreaDisplayText(formula.value, storageArea),
      });
    }

    if (updates.length > 0) {
      await updateDisplayTexts.execute({ storageAreas: updates });
    }

    toast.add({
      severity: 'success',
      summary: translations.value.generic.success,
      detail: translations.value.systemSettings.page.settingsSaved,
      life: 3000,
    });

    visible.value = false;
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <Dialog
    v-model:visible="visible"
    modal
    :header="translations.systemSettings.page.storagearea.displayTextFormulaTitle"
    :style="{ width: '40rem' }"
  >
    <div class="display-text-formula-dialog">
      <p class="description">
        {{ translations.systemSettings.page.storagearea.displayTextFormulaDescription }}
      </p>

      <div class="field">
        <label for="display-text-formula-editor">
          {{ translations.systemSettings.page.storagearea.displayTextFormula }}
        </label>
        <FormulaEditor
          id="display-text-formula-editor"
          :lang="shapeLanguage"
          v-model:modelValue="formula"
        ></FormulaEditor>
      </div>
    </div>

    <template #footer>
      <Button
        :label="translations.generic.cancel"
        severity="secondary"
        text
        :disabled="saving"
        @click="visible = false"
      ></Button>
      <Button
        :label="translations.generic.save"
        :loading="saving"
        @click="save"
      ></Button>
    </template>
  </Dialog>
</template>

<style scoped>
.display-text-formula-dialog {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.description {
  margin: 0;
  color: var(--p-text-muted-color);
}

.field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
</style>
